import { cn } from '@/shared/helpers/classnames'
import { getTypedBlocksByTypes } from '@/shared/helpers/compoundPattern'

import { type FC, useMemo } from 'react'

import {
  Content as RadixDialogContent,
  Overlay as RadixDialogOverlay,
  Portal as RadixDialogPortal,
  Root as RadixDialogRoot,
  Trigger as RadixDialogTrigger,
} from '@radix-ui/react-dialog'

import { DialogAction } from './DialogAction'
import { DialogActions } from './DialogActions'
import { DialogBody } from './DialogBody'
import { DialogCancelAction } from './DialogCancelAction'
import { DialogFooter } from './DialogFooter'
import { DialogHeader } from './DialogHeader'
import type { DialogActionBlocksTypes, DialogBlocksTypes, DialogProps } from './types'
import { DialogContext } from './useDialogContext'
import { useDialogDefaultButton } from './useDialogDefaultButton'

/**
 * Root Dialog component with Compound Pattern
 *
 * @example
 * ```tsx
 * <Dialog isOpen={isOpen} onOpenChange={setIsOpen} variant="default" size="lg">
 *   <Dialog.Header title="My title" />
 *   <Dialog.Body>Content</Dialog.Body>
 *   <Dialog.Actions>
 *     <Dialog.CancelAction />
 *     <Dialog.Action label="Confirm" intent="primary" onClick={handleConfirm} />
 *   </Dialog.Actions>
 * </Dialog>
 * ```
 */
const DialogRoot: FC<DialogProps> = ({
  isOpen,
  onOpenChange,
  variant = 'default',
  size = 'lg',
  closeOnBackdropClick = false,
  children,
}) => {
  // Hook to manage default button focus
  const { registerDefaultButton, registerCancelButton } = useDialogDefaultButton(isOpen)

  const { DialogHeader, DialogBody, DialogActions, DialogFooter } =
    getTypedBlocksByTypes<DialogBlocksTypes>(children)

  const contextValue = useMemo(() => {
    const handleClose = () => {
      onOpenChange?.(false)
    }

    // Extract cancel action and its callback
    const { DialogCancelAction, cancelCallback } = getDialogCancelAction(
      DialogActions?.props.children,
    )

    return {
      variant,
      size,
      onClose: handleClose,
      cancelCallback,
      hasOnlyCancelAction: hasOnlyCancelAction(DialogActions?.props.children, DialogCancelAction),
      registerDefaultButton,
      registerCancelButton,
    }
  }, [
    DialogActions?.props.children,
    variant,
    size,
    registerDefaultButton,
    registerCancelButton,
    onOpenChange,
  ])

  return (
    <DialogContext value={contextValue}>
      <RadixDialogRoot open={isOpen} onOpenChange={onOpenChange}>
        <RadixDialogPortal>
          <RadixDialogOverlay className="dialog-overlay" />
          <RadixDialogContent
            onEscapeKeyDown={event => {
              if (contextValue.cancelCallback) {
                event.preventDefault()
                contextValue.cancelCallback(event as unknown as React.KeyboardEvent)
              }
              contextValue.onClose()
            }}
            onInteractOutside={e => {
              if (!closeOnBackdropClick) {
                e.preventDefault()
              }
            }}
            className={cn('dialog-content', {
              'dialog-content-sm': size === 'sm',
              'dialog-content-md': size === 'md',
              'dialog-content-lg': size === 'lg',
              'dialog-content-xl': size === 'xl',
              'dialog-content-2xl': size === '2xl',
              'dialog-content-3xl': size === '3xl',
              'dialog-content-4xl': size === '4xl',
              'dialog-content-5xl': size === 'full',
            })}
          >
            {DialogHeader}
            {DialogBody}
            {DialogFooter}
            {DialogActions}
          </RadixDialogContent>
        </RadixDialogPortal>
      </RadixDialogRoot>
    </DialogContext>
  )
}
DialogRoot.displayName = 'Dialog'

/**
 * Extract and manage DialogCancelAction from Dialog actions
 * Separates concerns of managing cancel action logic
 *
 * @param actionsChildren - Children of DialogActions component
 * @returns Object containing DialogCancelAction component and its extracted callback
 */
const getDialogCancelAction = (actionsChildren: React.ReactNode) => {
  const { DialogCancelAction } = getTypedBlocksByTypes<DialogActionBlocksTypes>(actionsChildren)

  const cancelCallback = DialogCancelAction?.props?.onClick

  return {
    DialogCancelAction,
    cancelCallback,
  }
}

/**
 * Determine if only DialogCancelAction is present in Dialog actions
 * Separates concerns of validating Dialog action structure
 *
 * @param actionsChildren - Children of DialogActions component
 * @param DialogCancelAction - The DialogCancelAction component (if present)
 * @returns Boolean indicating if only cancel action is present
 */
const hasOnlyCancelAction = (
  actionsChildren: React.ReactNode,
  DialogCancelAction: React.ReactNode | undefined,
) => {
  return Boolean(
    actionsChildren &&
    DialogCancelAction &&
    (!Array.isArray(actionsChildren) || actionsChildren.length === 1),
  )
}

/**
 * Export du composant Dialog avec ses sous-composants (Compound Pattern)
 */
export const Dialog = Object.assign(DialogRoot, {
  Header: DialogHeader,
  Body: DialogBody,
  Actions: DialogActions,
  Action: DialogAction,
  CancelAction: DialogCancelAction,
  Footer: DialogFooter,
  Trigger: RadixDialogTrigger,
})
